import { useRef, type ChangeEvent, type DragEvent, type FC } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type {
  CatalogProductWorkspaceModeSwitchViewModel,
  ProductMediaManagerViewModel,
  ProductMediaItemViewModel,
  ProductMediaUploadViewModel,
  ProductMediaPlacementEditorViewModel,
  CommerceHelpCalloutViewModel,
} from "../../contracts/commerce-admin";
import { cn } from "../../lib/utils";
import {
  Callout,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  VerticalStack,
  SectionSeparator,
} from "../blocks";
import {
  ImageDown,
  Info,
  AlertCircle,
  X,
  Pencil,
  Trash,
  MoveUp,
  MoveDown,
  UploadCloud,
  CheckCircle,
  FileWarning,
  Save,
  Plus,
} from "lucide-react";

// Helper function for input styling, similar to inputClassName in CatalogEditorSheets
const inputClassName = "w-full rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:bg-background";
const buttonClassName = "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
const buttonPrimaryClassName = `${buttonClassName} bg-primary text-primary-foreground shadow hover:bg-primary/90`;
const buttonOutlineClassName = `${buttonClassName} border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground`;
const buttonGhostClassName = `${buttonClassName} hover:bg-accent hover:text-accent-foreground`;
const buttonDestructiveClassName = `${buttonClassName} bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90`;
const buttonIconClassName = "h-9 w-9 p-0";
const buttonSmallClassName = "h-8 px-3 text-xs";

interface CatalogProductWorkspaceModeSwitchProps {
  viewModel: CatalogProductWorkspaceModeSwitchViewModel;
  className?: string;
}

export const CatalogProductWorkspaceModeSwitch: FC<CatalogProductWorkspaceModeSwitchProps> = ({
  viewModel,
  className,
}) => {
  const { activeMode, variantCount, imageCount, onModeChange } = viewModel;

  return (
    <div
      className={cn(
        "flex space-x-1 border-b border-border text-sm font-medium text-muted-foreground",
        className
      )}
      role="tablist"
    >
      <button
        type="button"
        role="tab"
        id="tab-variants"
        aria-controls="variants-panel"
        aria-selected={activeMode === "variants"}
        className={cn(
          "px-3 py-2 -mb-px border-b-2",
          activeMode === "variants"
            ? "border-primary text-foreground"
            : "border-transparent hover:border-muted-foreground hover:text-foreground"
        )}
        onClick={() => onModeChange("variants")}
      >
        Variants ({variantCount})
      </button>
      <button
        type="button"
        role="tab"
        id="tab-images"
        aria-controls="images-panel"
        aria-selected={activeMode === "images"}
        className={cn(
          "px-3 py-2 -mb-px border-b-2",
          activeMode === "images"
            ? "border-primary text-foreground"
            : "border-transparent hover:border-muted-foreground hover:text-foreground"
        )}
        onClick={() => onModeChange("images")}
      >
        Images ({imageCount})
      </button>
    </div>
  );
};

interface CatalogProductMediaManagerProps {
  viewModel: ProductMediaManagerViewModel;
  className?: string;
}

export const CatalogProductMediaManager: FC<CatalogProductMediaManagerProps> = ({
  viewModel,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorFileInputRef = useRef<HTMLInputElement>(null);
  const editorRestoreFocusRef = useRef<HTMLElement | null>(null);
  const {
    state,
    title,
    description,
    emptyTitle,
    emptyBody,
    errorMessage,
    selectedPlacementId,
    items,
    callouts,
    upload,
    placementEditor,
    onRetryLoad,
    onSelectAsset,
    onChooseFile,
    onDropFile,
    onClearFile,
    onUpload,
    onRetryUpload,
    onPreviewError,
    onRetryPreview,
    onAddAsset,
    onEditAsset,
    onDeleteAsset,
    onReorder,
  } = viewModel;

  const selectedItem = items.find((item) => item.placementId === selectedPlacementId);
  const isEditingPlacement = placementEditor.isOpen;
  const uploadBusy = upload.status === "validating" ||
    upload.status === "initializing" ||
    upload.status === "uploading" ||
    upload.status === "completing";

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadBusy) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      editorRestoreFocusRef.current = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
      onDropFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    onChooseFile(e.target.files?.[0] ?? null);
    e.target.value = "";
  };

  const renderHelpText = (help?: { label: string; body: string; tone?: string }) => {
    if (!help) return null;
    const toneClass = help.tone ? {
      "text-destructive": help.tone === "danger",
      "text-warning": help.tone === "warning",
      "text-info": help.tone === "info",
      "text-success": help.tone === "success",
    }[help.tone] : "text-muted-foreground";

    return (
      <details className="relative inline-flex">
        <summary
          aria-label={`${help.label} help`}
          className="flex cursor-pointer list-none items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className={cn("h-3.5 w-3.5", toneClass)} aria-hidden="true" />
        </summary>
        <div className="absolute left-0 top-full z-10 mt-2 w-64 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
          <p className="font-semibold">{help.label}</p>
          <p className="mt-1 text-popover-foreground/80">{help.body}</p>
        </div>
      </details>
    );
  };

  // Adapted EditorChrome for the placement editor
  const renderPlacementEditorSheet = (editor: ProductMediaPlacementEditorViewModel) => {
    const fieldErrors = editor.validationErrors;
    const getFieldError = (fieldName: string) => fieldErrors[fieldName];

    if (!editor.isOpen) return null;

    return (
      <DialogPrimitive.Root
        open={editor.isOpen}
        onOpenChange={(open) => {
          if (!open) editor.onClose();
        }}
      >
        <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-200"
        />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl sm:max-w-2xl animate-in slide-in-from-right duration-300"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            editorRestoreFocusRef.current?.focus();
          }}
        >
          <DialogPrimitive.Title className="sr-only">{editor.title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {editor.mode === "create" ? "Add a new media placement" : "Edit media placement details"}
          </DialogPrimitive.Description>
          <PaneHeader title={editor.title} subtitle={editor.mode === "create" ? "Add a new media placement" : "Edit media placement details"} onClear={editor.onClose} />
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <VerticalStack gap={6}>
              {editor.errorMessage && (
                <Callout variant="error">
                  <div>
                    <p className="font-semibold">Error</p>
                    <p>{editor.errorMessage}</p>
                  </div>
                </Callout>
              )}
              {editor.mode === "create" && (
                <div className="rounded-md border border-border/60 bg-muted/10 p-4">
                  <input
                    type="file"
                    ref={editorFileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    aria-label="Choose product image in editor"
                    accept={upload.acceptedMimeTypes.join(",")}
                    disabled={uploadBusy}
                  />
                  {upload.selectedFile ? (
                    <div className="grid gap-4 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center">
                      <img
                        src={upload.selectedFile.localPreviewUrl}
                        alt="Selected local image preview"
                        className="aspect-square w-full rounded-md border border-border/60 bg-background object-contain"
                      />
                      <div className="min-w-0 space-y-2">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {upload.selectedFile.fileName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {upload.selectedFile.fileSizeLabel} · {upload.selectedFile.mimeTypeLabel}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={cn(buttonOutlineClassName, buttonSmallClassName)}
                            onClick={() => editorFileInputRef.current?.click()}
                            disabled={uploadBusy}
                          >
                            Choose another
                          </button>
                          <button
                            type="button"
                            className={cn(buttonGhostClassName, buttonSmallClassName)}
                            onClick={onClearFile}
                            disabled={uploadBusy}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-center">
                      <p className="text-sm font-semibold text-foreground">Choose an image to upload</p>
                      <p className="text-xs text-muted-foreground">
                        {upload.acceptedTypesLabel} up to {upload.maxFileSizeLabel}
                      </p>
                      <button
                        type="button"
                        className={cn(buttonOutlineClassName, buttonSmallClassName)}
                        onClick={() => editorFileInputRef.current?.click()}
                        disabled={uploadBusy}
                      >
                        Choose Image
                      </button>
                    </div>
                  )}
                  {uploadBusy && (
                    <div className="mt-4 space-y-2" aria-live="polite">
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="font-semibold text-foreground">{upload.statusLabel || "Uploading image"}</span>
                        <span className="text-muted-foreground">{upload.progressPercent}%</span>
                      </div>
                      <progress
                        value={upload.progressPercent}
                        max="100"
                        aria-label="Image upload progress"
                        className="h-2 w-full overflow-hidden rounded-full bg-muted [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
                      />
                    </div>
                  )}
                  {upload.errorMessage && (
                    <p className="mt-3 text-xs font-semibold text-destructive" role="alert">
                      {upload.errorMessage}
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-4">
                {/* Role */}
                <VerticalStack gap={2}>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="role" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                      Role
                    </label>
                    {renderHelpText(editor.fieldHelp.find(h => h.field === "role"))}
                  </div>
                  <select
                    id="role"
                    value={editor.value.role}
                    onChange={(e) => editor.onChange({ role: e.target.value })}
                    className={cn(inputClassName, getFieldError("role") && "border-destructive")}
                  >
                    {editor.roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {getFieldError("role") && <p className="text-[10px] font-bold text-destructive">{getFieldError("role")}</p>}
                </VerticalStack>

                {/* Applies To */}
                <VerticalStack gap={2}>
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                      Applies To
                    </label>
                    {renderHelpText(editor.fieldHelp.find(h => h.field === "appliesTo"))}
                  </div>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="radio"
                        name="appliesTo"
                        value="product"
                        checked={editor.value.appliesTo === "product"}
                        onChange={(e) => editor.onChange({ appliesTo: e.target.value as "product" | "variant" })}
                        className="h-4 w-4 text-primary focus:ring-primary border-border"
                      />
                      <span>Product</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="radio"
                        name="appliesTo"
                        value="variant"
                        checked={editor.value.appliesTo === "variant"}
                        onChange={(e) => editor.onChange({ appliesTo: e.target.value as "product" | "variant" })}
                        className="h-4 w-4 text-primary focus:ring-primary border-border"
                      />
                      <span>Variant</span>
                    </label>
                  </div>
                  {getFieldError("appliesTo") && <p className="text-[10px] font-bold text-destructive">{getFieldError("appliesTo")}</p>}
                </VerticalStack>

                {/* Variant Selector (if appliesTo is variant) */}
                {editor.value.appliesTo === "variant" && (
                  <VerticalStack gap={2}>
                    <div className="flex items-center gap-1.5">
                      <label htmlFor="variantId" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                        Variant
                      </label>
                      {renderHelpText(editor.fieldHelp.find(h => h.field === "variantId"))}
                    </div>
                    <select
                      id="variantId"
                      value={editor.value.variantId || ""}
                      onChange={(e) => editor.onChange({ variantId: e.target.value || undefined })}
                      className={cn(inputClassName, getFieldError("variantId") && "border-destructive")}
                    >
                      <option value="">Select a variant</option>
                      {editor.variantOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {getFieldError("variantId") && <p className="text-[10px] font-bold text-destructive">{getFieldError("variantId")}</p>}
                  </VerticalStack>
                )}

                {/* Alt Text */}
                <VerticalStack gap={2}>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="altText" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                      Alt Text
                    </label>
                    {renderHelpText(editor.fieldHelp.find(h => h.field === "altText"))}
                  </div>
                  <textarea
                    id="altText"
                    value={editor.value.altText || ""}
                    onChange={(e) => editor.onChange({ altText: e.target.value })}
                    className={cn(inputClassName, getFieldError("altText") && "border-destructive")}
                    rows={3}
                  />
                  {getFieldError("altText") && <p className="text-[10px] font-bold text-destructive">{getFieldError("altText")}</p>}
                </VerticalStack>

                {/* Caption */}
                <VerticalStack gap={2}>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="caption" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                      Caption
                    </label>
                    {renderHelpText(editor.fieldHelp.find(h => h.field === "caption"))}
                  </div>
                  <textarea
                    id="caption"
                    value={editor.value.caption || ""}
                    onChange={(e) => editor.onChange({ caption: e.target.value })}
                    className={cn(inputClassName, getFieldError("caption") && "border-destructive")}
                    rows={3}
                  />
                  {getFieldError("caption") && <p className="text-[10px] font-bold text-destructive">{getFieldError("caption")}</p>}
                </VerticalStack>

                {/* Order */}
                <VerticalStack gap={2}>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="sortOrder" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                      Order
                    </label>
                    {renderHelpText(editor.fieldHelp.find(h => h.field === "sortOrder"))}
                  </div>
                  <input
                    id="sortOrder"
                    type="number"
                    value={editor.value.sortOrder}
                    onChange={(e) => editor.onChange({ sortOrder: parseInt(e.target.value, 10) || 0 })}
                    className={cn(inputClassName, getFieldError("sortOrder") && "border-destructive")}
                  />
                  {getFieldError("sortOrder") && <p className="text-[10px] font-bold text-destructive">{getFieldError("sortOrder")}</p>}
                </VerticalStack>
              </div>
            </VerticalStack>
          </div>
          <PaneFooter>
            <HorizontalStack gap={2} className="justify-end">
              <button
                type="button"
                onClick={editor.onClose}
                disabled={editor.saving}
                className={cn(buttonOutlineClassName, buttonSmallClassName)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void editor.onSave()}
                disabled={editor.saving || (editor.mode === "create" && upload.submitDisabled)}
                className={cn(buttonPrimaryClassName, buttonSmallClassName)}
              >
                <Save className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                {editor.saving
                  ? "Saving…"
                  : editor.mode === "create" && upload.canRetry
                    ? "Retry upload"
                    : editor.saveActionLabel}
              </button>
            </HorizontalStack>
          </PaneFooter>
        </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  };

  const renderUploadArea = (uploadVm: ProductMediaUploadViewModel) => {
    return (
      <div
        className={cn(
          "rounded-md border border-dashed p-4 text-center",
          uploadVm.errorMessage ? "border-destructive" : "border-border",
          uploadBusy && "opacity-60"
        )}
        role="group"
        aria-label="Product image upload"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          aria-label="Choose product image"
          accept={uploadVm.acceptedMimeTypes.join(",")}
          disabled={uploadBusy}
        />
        {uploadVm.status === "idle" && (
          <div className="flex flex-col items-center justify-center space-y-2">
            <UploadCloud className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">Drop an image here or choose one from your device.</p>
            <p className="text-xs text-muted-foreground">
              {uploadVm.acceptedTypesLabel} up to {uploadVm.maxFileSizeLabel}
            </p>
            <button
              type="button"
              className={cn(buttonOutlineClassName, buttonSmallClassName)}
              onClick={(event) => {
                editorRestoreFocusRef.current = event.currentTarget;
                fileInputRef.current?.click();
              }}
              disabled={uploadBusy}
            >
              Choose Image
            </button>
          </div>
        )}
        {uploadVm.status === "selected" && uploadVm.selectedFile && (
          <div className="flex flex-col items-center space-y-2">
            <p className="text-sm font-medium">{uploadVm.selectedFile.fileName}</p>
            <p className="text-xs text-muted-foreground">
              ({uploadVm.selectedFile.fileSizeLabel}, {uploadVm.selectedFile.mimeTypeLabel})
            </p>
            {uploadVm.selectedFile.localPreviewUrl && (
              <img
                src={uploadVm.selectedFile.localPreviewUrl}
                alt="Local file preview"
                className="max-h-24 w-auto object-contain rounded-md"
              />
            )}
            <div className="flex space-x-2">
              <button
                type="button"
                className={cn(buttonOutlineClassName, buttonSmallClassName)}
                onClick={(e) => { e.stopPropagation(); onClearFile(); }}
              >
                <X className="w-4 h-4 mr-1" aria-hidden="true" /> Clear
              </button>
              <button
                type="button"
                className={cn(buttonPrimaryClassName, buttonSmallClassName)}
                onClick={(e) => { e.stopPropagation(); void onUpload(); }}
                disabled={uploadVm.submitDisabled}
              >
                <UploadCloud className="w-4 h-4 mr-1" aria-hidden="true" /> Upload
              </button>
            </div>
          </div>
        )}
        {(uploadVm.status === "validating" ||
          uploadVm.status === "initializing" ||
          uploadVm.status === "uploading" ||
          uploadVm.status === "completing") && (
            <div className="flex flex-col items-center space-y-2">
              <UploadCloud className="w-8 h-8 text-primary" aria-hidden="true" />
              <p className="text-sm font-medium">{uploadVm.statusLabel || "Uploading..."}</p>
              <progress
                value={uploadVm.progressPercent}
                max="100"
                aria-label="Image upload progress"
                aria-valuetext={uploadVm.statusLabel || `${uploadVm.progressPercent}% uploaded`}
                className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-muted [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
              />
              <p className="text-xs text-muted-foreground">
                {uploadVm.bytesTransferred} / {uploadVm.totalBytes}
              </p>
            </div>
          )}
        {uploadVm.status === "success" && (
          <div className="flex flex-col items-center space-y-2 text-success">
            <CheckCircle className="w-8 h-8" aria-hidden="true" />
            <p className="text-sm font-medium">{uploadVm.statusLabel || "Upload Complete!"}</p>
            <button
              type="button"
              className={cn(buttonOutlineClassName, buttonSmallClassName)}
              onClick={(e) => { e.stopPropagation(); onClearFile(); }}
            >
              <X className="w-4 h-4 mr-1" aria-hidden="true" /> Done
            </button>
          </div>
        )}
        {uploadVm.status === "error" && (
          <div className="flex flex-col items-center space-y-2 text-destructive">
            <FileWarning className="w-8 h-8" aria-hidden="true" />
            <p className="text-sm font-medium">{uploadVm.statusLabel || "Upload Failed"}</p>
            {uploadVm.errorMessage && <p className="text-xs">{uploadVm.errorMessage}</p>}
            <div className="flex space-x-2">
              <button
                type="button"
                className={cn(buttonOutlineClassName, buttonSmallClassName)}
                onClick={(e) => { e.stopPropagation(); onClearFile(); }}
              >
                <X className="w-4 h-4 mr-1" aria-hidden="true" /> Clear
              </button>
              {uploadVm.canRetry && (
                <button
                  type="button"
                  className={cn(buttonPrimaryClassName, buttonSmallClassName)}
                  onClick={(e) => { e.stopPropagation(); void onRetryUpload(); }}
                >
                  <UploadCloud className="w-4 h-4 mr-1" aria-hidden="true" /> Retry
                </button>
              )}
            </div>
          </div>
        )}
        {uploadVm.errorMessage && uploadVm.status !== "error" && (
          <p className="text-destructive text-sm mt-2">{uploadVm.errorMessage}</p>
        )}
      </div>
    );
  };

  const renderMediaGallery = (items: ProductMediaItemViewModel[]) => {
    const moveItem = (arr: ProductMediaItemViewModel[], fromIndex: number, toIndex: number) => {
      const newArr = [...arr];
      const [movedItem] = newArr.splice(fromIndex, 1);
      newArr.splice(toIndex, 0, movedItem);
      return newArr;
    };

    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {items.map((item, index) => (
          <div
            key={item.placementId}
            className={cn(
              "relative aspect-square overflow-hidden rounded-md border",
              selectedPlacementId === item.placementId
                ? "border-primary ring-2 ring-primary"
                : "border-border hover:border-muted-foreground"
            )}
          >
            <button
              type="button"
              className="absolute inset-0 flex h-full w-full items-center justify-center bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              onClick={() => onSelectAsset(item.placementId)}
              aria-label={`Select ${item.fileName}`}
              aria-pressed={selectedPlacementId === item.placementId}
            >
              {item.previewState === "loading" ? (
                <span className="absolute inset-0 animate-pulse bg-muted" aria-label="Loading preview" />
              ) : null}
              {item.previewState === "error" ? (
                <span className="absolute inset-0 flex flex-col items-center justify-center bg-destructive/10 p-2 text-center text-destructive">
                  <AlertCircle className="mb-1 h-5 w-5" aria-hidden="true" />
                  <span className="text-xs font-medium">Preview unavailable</span>
                </span>
              ) : null}
              {item.mediaUrl && item.previewState !== "error" && item.previewState !== "loading" ? (
                <img
                  src={item.mediaUrl}
                  alt={item.altText || item.fileName}
                  className="h-full w-full object-contain"
                  onError={() => onPreviewError(item.placementId)}
                />
              ) : null}
              <span className="absolute bottom-1 right-1 rounded-sm bg-background/90 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-muted-foreground">
                {item.role}
              </span>
            </button>
            {selectedPlacementId === item.placementId && (
              <div className="absolute top-1 right-1 flex space-x-1 z-10">
                <button
                  type="button"
                  className={cn(buttonOutlineClassName, buttonIconClassName, "h-10 w-10 bg-background/80 p-2 hover:bg-background md:h-8 md:w-8 md:p-1.5")}
                  onClick={(e) => {
                    e.stopPropagation();
                    editorRestoreFocusRef.current = e.currentTarget;
                    onEditAsset(item.placementId);
                  }}
                  aria-label={`Edit ${item.fileName} placement`}
                >
                  <Pencil className="w-4 h-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={cn(buttonOutlineClassName, buttonIconClassName, "h-10 w-10 bg-background/80 p-2 text-destructive hover:bg-background hover:text-destructive md:h-8 md:w-8 md:p-1.5")}
                  onClick={(e) => { e.stopPropagation(); onDeleteAsset(item.placementId); }}
                  aria-label={`Delete ${item.fileName}`}
                >
                  <Trash className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            )}
            <div className="absolute bottom-1 left-1 z-10 flex space-x-1">
              {index > 0 && (
                <button
                  type="button"
                  className={cn(buttonOutlineClassName, buttonIconClassName, "h-10 w-10 bg-background/80 p-2 hover:bg-background md:h-8 md:w-8 md:p-1.5")}
                  onClick={(e) => {
                    e.stopPropagation();
                    const reordered = moveItem(items, index, index - 1);
                    onReorder(reordered.map((i) => i.placementId));
                  }}
                  aria-label={`Move ${item.fileName} earlier`}
                >
                  <MoveUp className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
              {index < items.length - 1 && (
                <button
                  type="button"
                  className={cn(buttonOutlineClassName, buttonIconClassName, "h-10 w-10 bg-background/80 p-2 hover:bg-background md:h-8 md:w-8 md:p-1.5")}
                  onClick={(e) => {
                    e.stopPropagation();
                    const reordered = moveItem(items, index, index + 1);
                    onReorder(reordered.map((i) => i.placementId));
                  }}
                  aria-label={`Move ${item.fileName} later`}
                >
                  <MoveDown className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSelectedPreview = (item: ProductMediaItemViewModel) => {
    return (
      <div className="flex flex-col gap-4">
        <div className="relative border rounded-md overflow-hidden aspect-video flex items-center justify-center bg-muted/20">
          {item.previewState === "loading" && (
            <div className="h-full w-full animate-pulse bg-muted" aria-label="Loading preview" />
          )}
          {item.previewState === "error" && (
            <div className="flex flex-col items-center justify-center text-destructive p-4 text-center">
              <AlertCircle className="w-8 h-8 mb-2" aria-hidden="true" />
              <p className="font-semibold">Preview Error</p>
              <p className="text-sm">{item.previewErrorMessage || "Could not load preview."}</p>
              <button
                type="button"
                className={cn(buttonGhostClassName)}
                onClick={() => onRetryPreview(item.placementId)}
              >
                Retry
              </button>
            </div>
          )}
          {item.mediaUrl && item.previewState !== "error" && item.previewState !== "loading" && (
            <img
              src={item.mediaUrl}
              alt={item.altText || item.fileName}
              className="object-contain w-full h-full"
              onError={() => onPreviewError(item.placementId)}
            />
          )}
          {item.previewState === "refreshing" && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-background/50"
              aria-label="Refreshing preview"
            >
              <div className="h-full w-full animate-pulse bg-muted" />
            </div>
          )}
        </div>
        <div className="grid grid-cols-[minmax(6rem,auto)_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
          <p className="font-medium">Role:</p>
          <p className="text-muted-foreground">{item.role || "N/A"}</p>

          {item.variantLabel && (
            <>
              <p className="font-medium">Applies To:</p>
              <p className="text-muted-foreground">Variant: {item.variantLabel}</p>
            </>
          )}
          {!item.variantLabel && (
            <>
              <p className="font-medium">Applies To:</p>
              <p className="text-muted-foreground">Product</p>
            </>
          )}

          <p className="font-medium">Alt Text:</p>
          <p className="text-muted-foreground">{item.altText || "N/A"}</p>

          <p className="font-medium">Caption:</p>
          <p className="text-muted-foreground">{item.caption || "N/A"}</p>

          <p className="font-medium">Order:</p>
          <p className="text-muted-foreground">{item.sortOrder}</p>

          <p className="font-medium">Filename:</p>
          <p className="break-words text-muted-foreground">{item.fileName}</p>

          <p className="font-medium">Type:</p>
          <p className="text-muted-foreground">{item.mimeTypeLabel || "N/A"}</p>

          <p className="font-medium">Dimensions:</p>
          <p className="text-muted-foreground">{item.dimensionsLabel || "N/A"}</p>

          <p className="font-medium">Size:</p>
          <p className="text-muted-foreground">{item.fileSizeLabel || "N/A"}</p>

          <p className="font-medium">Upload Origin:</p>
          <p className="text-muted-foreground">{item.uploadOriginLabel || "N/A"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={cn(buttonOutlineClassName, buttonSmallClassName)}
            onClick={(event) => {
              editorRestoreFocusRef.current = event.currentTarget;
              onEditAsset(item.placementId);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" aria-hidden="true" /> Edit Placement
          </button>
          <button
            type="button"
            className={cn(buttonDestructiveClassName, buttonSmallClassName)}
            onClick={() => onDeleteAsset(item.placementId)}
          >
            <Trash className="mr-2 h-4 w-4" aria-hidden="true" /> Delete Image
          </button>
        </div>
      </div>
    );
  };

  const renderCallouts = (callouts: CommerceHelpCalloutViewModel[]) => (
    <div className="space-y-2">
      {callouts.map((callout) => (
        <Callout
          key={callout.id}
          variant={callout.tone === "danger" ? "error" : callout.tone}
        >
          <div>
            <p className="font-semibold">{callout.title}</p>
            <p>{callout.body}</p>
          </div>
        </Callout>
      ))}
    </div>
  );

  if (state === "loading") {
    return (
      <div className={cn("space-y-4 p-4", className)} aria-label="Loading product images">
        <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-32 animate-pulse rounded bg-muted" />
          <div className="h-32 animate-pulse rounded bg-muted" />
          <div className="h-32 animate-pulse rounded bg-muted" />
        </div>
        <div className="h-10 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className={cn("p-4 flex flex-col items-center justify-center text-center space-y-4", className)}>
        <AlertCircle className="w-12 h-12 text-destructive" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-destructive">Error Loading Media</h3>
        <p className="text-muted-foreground">{errorMessage || "An unexpected error occurred."}</p>
        <button
          type="button"
          className={cn(buttonPrimaryClassName)}
          onClick={onRetryLoad}
        >
          Retry Load
        </button>
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className={cn("p-4 flex flex-col items-center justify-center text-center space-y-4", className)}>
        <ImageDown className="w-12 h-12 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-lg font-semibold">{emptyTitle}</h3>
        <p className="text-muted-foreground">{emptyBody}</p>
        {renderUploadArea(upload)}
        {callouts.length > 0 && renderCallouts(callouts)}
        {isEditingPlacement && renderPlacementEditorSheet(placementEditor)}
      </div>
    );
  }

  return (
    <div className={cn("p-4 grid grid-cols-1 md:grid-cols-3 gap-6", className)}>
      <div className="md:col-span-2 flex flex-col gap-6">
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="text-muted-foreground">{description}</p>

        {callouts.length > 0 && renderCallouts(callouts)}

        <SectionSeparator />

        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Media Gallery ({items.length})</h3>
          <button
            type="button"
            className={cn(buttonPrimaryClassName)}
            onClick={(event) => {
              editorRestoreFocusRef.current = event.currentTarget;
              onAddAsset();
            }}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> Upload Image
          </button>
        </div>
        {items.length > 0 ? (
          renderMediaGallery(items)
        ) : (
          <div className="text-center text-muted-foreground py-8 border rounded-md">
            Select an image from the gallery to see details.
          </div>
        )}

        <div className="flex flex-col gap-6 md:hidden">
          <h3 className="text-lg font-semibold">Selected Media Details</h3>
          {selectedItem ? (
            renderSelectedPreview(selectedItem)
          ) : (
            <div className="rounded-md border py-8 text-center text-muted-foreground">
              Select an image from the gallery to see details.
            </div>
          )}
        </div>

        <SectionSeparator />

        <h3 className="text-lg font-semibold">Upload New Media</h3>
        {renderUploadArea(upload)}
      </div>

      <div className="hidden flex-col gap-6 md:col-span-1 md:flex">
        <h3 className="text-lg font-semibold">Selected Media Details</h3>
        {selectedItem ? (
          renderSelectedPreview(selectedItem)
        ) : (
          <div className="text-center text-muted-foreground py-8 border rounded-md">
            Select an image from the gallery to see details.
          </div>
        )}
      </div>

      {isEditingPlacement && renderPlacementEditorSheet(placementEditor)}
    </div>
  );
};
